import { FileText, MessageSquare, User } from 'lucide-react';
import type { PathResolver } from '@/components/layout/workspace/types';
import type { Attachment } from '@/lib/attachments';
import type { ConversationThread } from '@/lib/conversation';
import type { MessageEntity, MessageText } from '@/lib/messageMarkup';

export const HOME = '/design/conversations';
export const DEMO_AUTHOR: MessageEntity = { id: 'demo', kind: 'user', name: 'Демо Пользователь' };
export const ENTITIES: MessageEntity[] = [
  { id: 'anna', kind: 'user', name: 'Анна Смирнова' },
  { id: 'mira', kind: 'user', name: 'Мира Белова' },
  { id: 'elena', kind: 'user', name: 'Елена Петрова' },
  { id: 'danil', kind: 'user', name: 'Данил Романов' },
  { id: 'workshop', kind: 'channel', name: 'мастерская' },
];
const authors = ENTITIES.filter((entity) => entity.kind === 'user');
export const FILES: Attachment[] = [
  {
    id: 'demo-pdf',
    name: 'Счёт № 042.pdf',
    size: 81639,
    mime: 'application/pdf',
    url: '/design-fixtures/sample-invoice.pdf',
    state: 'ready',
  },
  {
    id: 'demo-image',
    name: 'Счёт № 042.png',
    size: 34887,
    mime: 'image/png',
    url: '/design-fixtures/sample-invoice.png',
    state: 'ready',
  },
];
const sources = [
  { label: 'Чат', channel: '#мастерская' },
  { label: 'Почта', channel: 'Встреча команды' },
  { label: 'Обсуждение', channel: 'Общий макет' },
];
const sent = (minutes: number) => new Date(Date.UTC(2026, 9, 23, 6, 40 + minutes)).toISOString();
const chat = (text: string): MessageText => ({ text, format: 'chat' });
const markdown = (text: string): MessageText => ({ text, format: 'markdown' });
export const THREADS: ConversationThread[] = [
  {
    message: {
      id: 'welcome',
      author: authors[0],
      sentAt: sent(0),
      source: sources[0],
      content: chat(
        'Доброе утро! :wave: Собираем *общий макет* в <#workshop>.\n<@mira>, посмотри _отступы_ и ~старую подпись~ новую формулировку.',
      ),
      reactions: [
        {
          id: 'wave',
          emoji: '👋',
          label: 'Приветствие',
          count: 3,
          people: ['Мира Белова', 'Елена Петрова', 'Данил Романов'],
        },
        {
          id: 'like',
          emoji: '👍',
          label: 'Нравится',
          count: 2,
          selected: true,
          people: ['Демо Пользователь', 'Мира Белова'],
        },
      ],
      editedAt: sent(2),
    },
    replies: [
      {
        id: 'welcome-r1',
        author: authors[1],
        sentAt: sent(3),
        content: chat('Да, посмотрю. Ссылку на результат оставлю в <#workshop>.'),
      },
      { id: 'welcome-r2', author: authors[0], sentAt: sent(5), content: chat('Спасибо! :sparkles:') },
    ],
  },
  {
    message: {
      id: 'code',
      author: authors[1],
      sentAt: sent(7),
      source: sources[0],
      content: markdown(
        [
          '**Проверка готова.** Состояние хранится в \x60draft\x60; пример запроса:',
          '',
          '\x60\x60\x60ts',
          '// Вымышленный пример — без отправки запроса',
          'const draft = { title: "Новый макет", ready: true };',
          'const endpoint = "https://example.test/api/drafts";',
          'console.log(draft.title, endpoint);',
          '\x60\x60\x60',
          '',
          'Описание — в [заметке о макете](https://example.test/layout). ✅',
        ].join('\n'),
      ),
      reactions: [
        { id: 'eyes', emoji: '👀', label: 'Смотрю', count: 2, people: ['Анна Смирнова', 'Данил Романов'] },
      ],
      receipt: 'read',
    },
  },
  {
    message: {
      id: 'files',
      author: authors[2],
      sentAt: sent(11),
      source: sources[1],
      quote: {
        author: 'Анна Смирнова',
        content: markdown('Добавим пример вложения, чтобы проверить просмотр рядом с перепиской.'),
      },
      content: markdown(
        'Прикрепила **выдуманный счёт** и его изображение. <@anna>, оба открываются рядом с сообщением.',
      ),
      attachments: FILES,
      receipt: 'delivered',
    },
  },
  {
    message: {
      id: 'discussion',
      author: authors[3],
      sentAt: sent(16),
      source: sources[2],
      content: markdown(
        'Предлагаю оставить **один вид сообщения** для всех источников.\nИмена людей, время и ветки будут читаться одинаково. :sparkles:',
      ),
      reactions: [{ id: 'like', emoji: '👍', label: 'Нравится', count: 3 }],
    },
    replies: [
      {
        id: 'discussion-r1',
        author: authors[0],
        sentAt: sent(18),
        content: markdown('Поддерживаю. Разницу показывает только подпись источника.'),
      },
      {
        id: 'discussion-r2',
        author: authors[1],
        sentAt: sent(21),
        content: chat('Готово, <@danil>. Ветку можно свернуть и продолжить чтение. :white_check_mark:'),
        receipt: 'read',
      },
    ],
  },
  ...Array.from({ length: 44 }, (_, i): ConversationThread => ({
    message: {
      id: 'history-' + (i + 1),
      author: authors[i % authors.length],
      sentAt: sent(24 + i * 3),
      source: sources[i % sources.length],
      content: {
        format: 'plain',
        text: 'Заметка к макету № ' + (i + 1) + '.\nСледующий фрагмент обсуждения готов.',
      },
    },
  })),
];

export const MARKUP_SAMPLES: { title: string; description: string; content: MessageText }[] = [
  {
    title: 'Текст и упоминания',
    description: 'Markdown',
    content: markdown(
      '**Полужирный**, *курсив*, ~~зачёркнутый~~ и \x60inline code\x60.\n<@anna>, загляни в <#workshop>. [Открыть заметку](https://example.test/note) :wave:',
    ),
  },
  {
    title: 'Внешний формат чата',
    description: 'Компактная разметка',
    content: chat(
      '*Полужирный*, _курсив_, ~зачёркнутый~, \x60draft.title\x60.\n<@mira> в <#workshop|мастерская>: <https://example.test/layout|пример макета> :sparkles: :unknown_emoji:',
    ),
  },
  {
    title: 'Код с указанным языком',
    description: 'JSON · JavaScript / TypeScript · Python · shell',
    content: markdown(
      [
        '\x60\x60\x60json',
        '{',
        '  "name": "Демо",',
        '  "ready": true,',
        '  "count": 12',
        '}',
        '\x60\x60\x60',
      ].join('\n'),
    ),
  },
  {
    title: 'Неизвестный язык кода',
    description: 'Текст сохраняется без предположений о языке',
    content: markdown(
      ['\x60\x60\x60custom', 'draft -> review -> ready', '  {{unknown_macro}}', '\x60\x60\x60'].join('\n'),
    ),
  },
  {
    title: 'Незнакомая и опасная разметка',
    description: 'Показывается текстом, без HTML и внешних картинок',
    content: markdown(
      '<script>alert("demo")</script>\n<img src="https://example.test/pixel" onerror="alert(1)">\n[небезопасная ссылка](javascript:alert(1))\n![внешняя картинка](https://example.test/pixel.png)\n<@unknown> · {{unknown_macro}}',
    ),
  },
  {
    title: 'Обычный текст',
    description: 'Источник без разметки',
    content: {
      format: 'plain',
      text: '**Звёздочки** и <@anna> остаются ровно как в источнике.\nЭмодзи Unicode работают всегда: 👋 ✨ 👍',
    },
  },
];

export const resolveConversation: PathResolver = (path) => {
  const id = path.split('/').at(-1);
  const file = FILES.find((file) => file.id === id);
  const entity =
    ENTITIES.find((entity) => entity.id === id) ?? (id === DEMO_AUTHOR.id ? DEMO_AUTHOR : undefined);
  return {
    type: path === HOME || path.startsWith(HOME + '?') ? 'view' : 'object',
    serviceId: 'conversation-examples',
    title: file?.name ?? entity?.name ?? (path.includes('/source/') ? 'Источник · Переписка' : 'Переписка'),
    icon: file ? FileText : entity?.kind === 'user' ? User : MessageSquare,
    homeView: HOME,
    trail: 'Образцы › Переписка',
  };
};
